import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Users, Hotel, Bus, Plane, Globe, ArrowUpRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const PRODUCT_IMAGES = {
  hotels: 'https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/qyjvb07x_Hoteis.png',
  circuits: 'https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/8ri432vo_Circuitos.png',
  flights: 'https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/nrsnpm3q_Voo%2BHotel.png',
  groups: 'https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/ffav2em9_Grupos.png',
  incoming: 'https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/mmk5j25p_Incoming.png',
  busHotel: 'https://customer-assets.emergentagent.com/job_experiencias-travel/artifacts/3x9zxop6_Bus%2BHotel.png',
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function Products() {
  const { t } = useLanguage();

  const products = [
    {
      id: 'groups',
      icon: Users,
      title: t('products.groups.title'),
      desc: t('products.groups.desc'),
      features: t('products.groups.features'),
      image: PRODUCT_IMAGES.groups,
      stats: { value: '1000+', label: 'Grupos/Ano' }
    },
    {
      id: 'hotels',
      icon: Hotel,
      title: t('products.hotels.title'),
      desc: t('products.hotels.desc'),
      features: t('products.hotels.features'),
      image: PRODUCT_IMAGES.hotels,
      stats: { value: '1000+', label: 'Hotéis' }
    },
    {
      id: 'circuits',
      icon: Bus,
      title: t('products.circuits.title'),
      desc: t('products.circuits.desc'),
      features: t('products.circuits.features'),
      image: PRODUCT_IMAGES.circuits,
      stats: { value: '50+', label: 'Circuitos' }
    },
    {
      id: 'flights',
      icon: Plane,
      title: t('products.flights.title'),
      desc: t('products.flights.desc'),
      features: t('products.flights.features'),
      image: PRODUCT_IMAGES.flights,
      stats: { value: '20+', label: 'Destinos' }
    },
    {
      id: 'incoming',
      icon: Globe,
      title: t('products.incoming.title'),
      desc: t('products.incoming.desc'),
      features: t('products.incoming.features'),
      image: PRODUCT_IMAGES.incoming,
      stats: { value: '500+', label: 'Grupos Internacionais' }
    },
    {
      id: 'outgoing',
      icon: Bus,
      title: t('products.outgoing.title'),
      desc: t('products.outgoing.desc'),
      features: t('products.outgoing.features'),
      image: PRODUCT_IMAGES.busHotel,
      stats: { value: '50+', label: 'Destinos' }
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00D4FF]/20 text-[#00D4FF] rounded-full text-sm font-medium mb-6">
              7x7 Travel
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('products.title')}
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('products.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                data-testid={`product-${product.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#00D4FF] flex items-center justify-center">
                      <product.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="px-4 py-2 bg-[#00D4FF]/10 text-[#1E3A8A] rounded-full text-sm font-semibold">
                      {product.stats.value} {product.stats.label}
                    </div>
                  </div>
                  
                  <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {product.title}
                  </h2>
                  
                  <p className="text-slate-600 text-lg leading-relaxed mb-6">
                    {product.desc}
                  </p>
                  
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                    {Array.isArray(product.features) && product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-slate-700">
                        <CheckCircle className="w-5 h-5 text-[#00D4FF] flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Link to="/group-request">
                    <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-6">
                      Pedir Orçamento
                      <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>

                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className={`absolute -bottom-6 ${index % 2 === 1 ? '-right-6' : '-left-6'} w-32 h-32 bg-[#00D4FF] rounded-2xl -z-10`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="products-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('home.ctaTitle')}
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              {t('home.ctaSubtitle')}
            </p>
            <Link to="/group-request">
              <Button
                size="lg"
                className="bg-[#00D4FF] hover:bg-[#00D4FF]/90 text-[#0F172A] rounded-full px-8 py-6 text-lg font-semibold"
              >
                Pedir Orçamento
                <ArrowUpRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
